import { useMemo, useState, type ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { LinkIcon, ShareNetworkIcon } from '@phosphor-icons/react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { downloadRebateMultiTableXlsx } from '@/components/rebates/reports/export/downloadRebateReportXlsx';
import { RebateReportDownloadMenu } from '@/components/rebates/reports/export/RebateReportDownloadMenu';
import {
  rebateMultiTableDownloadFilename,
  rebateMultiTableFinalFilename,
} from '@/components/rebates/reports/export/rebateMultiTableDownloadFilename';
import { DownloadModal } from 'src/components/reports/DownloadModal';
import { ShareReportModal } from 'src/components/reports/ShareReportModal';
import { SharedLinksManager } from 'src/components/reports/SharedLinksManager';
import { HeaderPageTitle } from 'src/context/header-title.context';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { downloadMultiTableCSV } from '@/utils/csvDownload';
import { PurchaseTrendChartSection } from './PurchaseTrendChartSection';
import { PurchaseTrendFilters } from './PurchaseTrendFilters';
import {
  PurchaseTrendFlags,
  PurchaseTrendNoAgreementSuppliersSection,
} from './PurchaseTrendFlags';
import { PurchaseTrendAppliedMappingsSection } from './PurchaseTrendAppliedMappingsSection';
import { PurchaseTrendPageHeader } from './PurchaseTrendPageHeader';
import {
  PurchaseTrendTablesSection,
  usePurchaseTrendTableLabels,
} from './PurchaseTrendTablesSection';
import { PurchaseTrendUploadSection } from './PurchaseTrendUploadSection';
import { PurchaseTrendFirstUpload } from './PurchaseTrendFirstUpload';
import {
  purchaseTrendExportBasename,
  purchaseTrendTablesToExport,
} from './purchaseTrendTablesToExport';
import type { PurchaseTrendTableView } from './purchaseTrendTables';
import {
  purchaseTrendDownloadModalCopy,
  purchaseTrendShareMetadata,
  type PurchaseTrendLockedFilters,
} from './purchaseTrendPage';
import { usePurchaseTrendPage } from './usePurchaseTrendPage';

const REPORT_ID = 'purchases-vs-rebates';

function PurchaseTrendPageShell({
  isShared,
  children,
}: {
  isShared: boolean;
  children: ReactNode;
}) {
  return isShared ? children : <DashboardLayout>{children}</DashboardLayout>;
}

function PurchaseTrendShareMenu({
  onCreate,
  onManage,
}: {
  onCreate: () => void;
  onManage: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <ShareNetworkIcon className="h-4 w-4 mr-2" />
          <T context="rebates">Share</T>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onCreate}>
          <ShareNetworkIcon className="h-4 w-4 mr-2" />
          <T context="menu item, create shareable report link">
            Create share link
          </T>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onManage}>
          <LinkIcon className="h-4 w-4 mr-2" />
          <T context="menu item, manage existing shared links">
            Manage shared links
          </T>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PurchasesVsRebatesLoading({
  isShared,
  reportTitle,
  loadingText,
}: {
  isShared: boolean;
  reportTitle: string;
  loadingText: string;
}) {
  return (
    <PurchaseTrendPageShell isShared={isShared}>
      <div className="p-1">
        <HeaderPageTitle>{reportTitle}</HeaderPageTitle>
        <div data-testid="purchase-trend-page-loading">
          <LoadingSpinner
            text={loadingText}
            centered
            size="xl"
            minHeight="60vh"
          />
        </div>
      </div>
    </PurchaseTrendPageShell>
  );
}

function PurchasesVsRebatesReady({
  page,
  reportTitle,
}: {
  page: ReturnType<typeof usePurchaseTrendPage>;
  reportTitle: string;
}) {
  const gt = useGT();
  const [tableView, setTableView] =
    useState<PurchaseTrendTableView>('divisions');
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<'csv' | 'xlsx'>('csv');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [linksManagerOpen, setLinksManagerOpen] = useState(false);
  const tableLabels = usePurchaseTrendTableLabels();
  const exportLabels = useMemo(
    () => ({
      ...tableLabels,
      divisions: gt('Divisions', {
        context: 'purchases vs rebates export section title',
      }),
      suppliers: gt('Suppliers', {
        context: 'purchases vs rebates export section title',
      }),
      total: gt('Total', {
        context: 'purchases vs rebates export totals row',
      }),
    }),
    [gt, tableLabels],
  );
  const exportBasename = purchaseTrendExportBasename({
    year: page.selectedYear,
    supplierLabels: page.selectedSuppliers.map((option) => option.label),
    divisionLabels: page.selectedDivisions.map((option) => option.label),
  });
  const canDownload = page.chartStatus === 'ready' && page.cells.length > 0;
  const downloadCopy = purchaseTrendDownloadModalCopy(downloadFormat, {
    xlsxTitle: gt('Download Purchases vs. Rebates (.xlsx)', {
      context: 'modal title for downloading purchases vs rebates as Excel',
    }),
    csvTitle: gt('Download Purchases vs. Rebates (.csv)', {
      context: 'modal title for downloading purchases vs rebates as CSV',
    }),
    xlsxConfirm: gt('Download Excel', { context: 'rebates' }),
    csvConfirm: gt('Download CSV', { context: 'rebates' }),
  });

  function handleDownloadClick(format: 'csv' | 'xlsx') {
    if (!canDownload) {
      return;
    }
    setDownloadFormat(format);
    setDownloadModalOpen(true);
  }

  function handleDownload(filename: string) {
    const options = {
      tables: purchaseTrendTablesToExport(page.cells, exportLabels),
      filename: rebateMultiTableFinalFilename(filename, downloadFormat),
    };
    if (downloadFormat === 'xlsx') {
      void downloadRebateMultiTableXlsx(options);
      return;
    }
    downloadMultiTableCSV(options);
  }

  return (
    <PurchaseTrendPageShell isShared={page.isShared}>
      <div className={page.isShared ? '' : 'p-1'}>
        {page.isShared ? (
          <h1 className="mb-2 text-2xl font-bold">{reportTitle}</h1>
        ) : (
          <PurchaseTrendPageHeader
            canUpload={page.canUpload}
            onUploadClick={() => page.setUploadOpen(true)}
          />
        )}
        <PurchaseTrendFlags
          forecastUnavailableReason={page.forecastUnavailableReason}
          unmatchedPurchaseSuppliers={page.unmatchedPurchaseSuppliers}
          unmatchedDivisionNames={page.unmatchedDivisionNames}
          unmatchedPurchaseDivisions={page.unmatchedPurchaseDivisions}
          supplierOptions={page.supplierOptions}
          divisionOptions={page.divisionOptions}
          isShared={page.isShared}
        />
        <PurchaseTrendChartSection
          status={page.chartStatus}
          rows={page.chartRows}
          title={page.title}
          subtitle={page.subtitle}
          filters={
            page.isShared ? null : (
              <PurchaseTrendFilters
                uploadOptions={page.uploadOptions}
                selectedUploadId={page.selectedUploadId}
                onUploadChange={page.setSelectedUploadId}
                canRemoveUploads={page.canRemoveUploads}
                onRemoveUpload={page.onRemoveUpload}
                removeUploadLoading={page.removeUploadLoading}
                supplierOptions={page.supplierOptions}
                selectedSuppliers={page.selectedSuppliers}
                onSuppliersChange={page.setSelectedSuppliers}
                divisionOptions={page.divisionOptions}
                selectedDivisions={page.selectedDivisions}
                onDivisionsChange={page.setSelectedDivisions}
              />
            )
          }
          reportedAlgorithm={page.reportedAlgorithm}
          supplierDataThrough={page.supplierDataThrough}
          lastUploadCard={page.lastUploadCard}
        />
        <PurchaseTrendTablesSection
          status={page.chartStatus}
          cells={page.cells}
          view={tableView}
          onViewChange={setTableView}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <RebateReportDownloadMenu
                disabled={!canDownload}
                onDownload={handleDownloadClick}
              />
              {page.isShared ? null : (
                <PurchaseTrendShareMenu
                  onCreate={() => setShareModalOpen(true)}
                  onManage={() => setLinksManagerOpen(true)}
                />
              )}
            </div>
          }
        />
        {page.isShared ? null : (
          <>
            <PurchaseTrendAppliedMappingsSection
              rows={page.appliedPurchaseMappings}
            />
            <PurchaseTrendNoAgreementSuppliersSection
              purchaseSuppliersWithoutRebateAgreement={
                page.purchaseSuppliersWithoutRebateAgreement
              }
            />
          </>
        )}
        {page.isShared ? null : (
          <>
            <PurchaseTrendUploadSection
              canUpload={page.canUpload}
              open={page.uploadOpen}
              onOpenChange={page.setUploadOpen}
              onImported={page.setLastUpload}
            />
            <ShareReportModal
              open={shareModalOpen}
              onClose={() => setShareModalOpen(false)}
              reportId={REPORT_ID}
              reportTitle={reportTitle}
              initialYear={page.selectedYear}
              initialDivisions={page.selectedDivisions}
              additionalMetadata={purchaseTrendShareMetadata(
                page.selectedUploadId,
              )}
              customFilters={[
                {
                  key: 'supplierIds',
                  label: gt('Suppliers', {
                    context: 'shared filter label',
                  }),
                  options: page.supplierOptions.map((option) => ({
                    id: option.id,
                    label: option.label,
                  })),
                  initialValue: page.selectedSuppliers.map(
                    (option) => option.id,
                  ),
                },
              ]}
            />
            <SharedLinksManager
              open={linksManagerOpen}
              onClose={() => setLinksManagerOpen(false)}
              reportId={REPORT_ID}
              reportTitle={reportTitle}
              onCreateNew={() => {
                setLinksManagerOpen(false);
                setShareModalOpen(true);
              }}
            />
          </>
        )}
        <DownloadModal
          open={downloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
          onDownload={handleDownload}
          defaultFilename={rebateMultiTableDownloadFilename(
            exportBasename,
            downloadFormat,
          )}
          title={downloadCopy.title}
          confirmLabel={downloadCopy.confirmLabel}
        />
      </div>
    </PurchaseTrendPageShell>
  );
}

export function PurchasesVsRebatesPage({
  lockedFilters,
}: {
  lockedFilters?: PurchaseTrendLockedFilters;
} = {}) {
  const page = usePurchaseTrendPage(lockedFilters);
  const gt = useGT();
  const reportTitle = gt('Purchases vs. Rebates', {
    context: 'rebates report page title',
  });
  if (page.isInitialLoading) {
    return (
      <PurchasesVsRebatesLoading
        isShared={page.isShared}
        reportTitle={reportTitle}
        loadingText={gt('Loading purchases and rebates...', {
          context: 'loading purchases versus rebates chart',
        })}
      />
    );
  }
  if (page.hasNoFile && page.canUpload) {
    return (
      <PurchaseTrendPageShell isShared={false}>
        <div className="p-1">
          <HeaderPageTitle>{reportTitle}</HeaderPageTitle>
          <PurchaseTrendFirstUpload
            open={page.uploadOpen}
            onOpenChange={page.setUploadOpen}
            onImported={page.setLastUpload}
          />
        </div>
      </PurchaseTrendPageShell>
    );
  }
  return <PurchasesVsRebatesReady page={page} reportTitle={reportTitle} />;
}

export default PurchasesVsRebatesPage;
